import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { AccountModal, PackagesSelectionModal } from '@/lazy-load-components';

import { useExperiment } from '@/libraries/experiment';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { useI18n } from '@/helpers/composables/useI18n';

import { useUserStore } from '@/stores/pinia/useUserStore';

import type { ComputedRef, InjectionKey, Ref } from 'vue';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';
import { AccountModes } from '@/enums/login';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { MaybeRefOrGetter, injectLocal, provideLocal, toValue, useTimeoutFn } from '@vueuse/core';
import { Package } from '@/@types/graphql-types';

export const PROVIDER_EXP_BB_PROVIDER_FILTER_KEY: InjectionKey<Ref<string[] | null>> = Symbol('buyboxProviderFilter');
export const PROVIDER_EXP_BB_OFFER_PROVIDERS_KEY: InjectionKey<ComputedRef<string[]>> = Symbol('buyboxOfferProviders');
export const PROVIDER_EXP_BB_SAVE_PROMPT_KEY: InjectionKey<Ref<boolean>> = Symbol('buyboxSavePrompt');
export const PROVIDER_EXP_BB_LAST_TYPE_KEY: InjectionKey<Ref<0 | 1>> = Symbol('buyboxLastType');
export const PROVIDER_EXP_BB_HAS_SHOWN_MODAL_KEY: InjectionKey<Ref<boolean>> = Symbol('buyboxHasShownModalForTitle');

export function useProviderBuyboxFilter(options?: {
	listFilterType?: MaybeRefOrGetter<BuyBoxFilterOptions | null>;
	offerProviders?: MaybeRefOrGetter<string[]>;
	isInternationalOffersActive?: MaybeRefOrGetter<boolean>;
	titleId?: MaybeRefOrGetter<string | undefined>;
}) {
	const { activeVariantWithControlGroup: providerBuyboxExpVariant, trackEvent } =
		useExperiment('EXP_PROVIDER_BUYBOX_V2');

	const userStore = useUserStore();
	const { settingsProvider } = storeToRefs(userStore);
	const { setSeenMyServices } = userStore;

	const { tc } = useI18n();

	const effectiveFilter = injectLocal<Ref<string[] | null>>(
		PROVIDER_EXP_BB_PROVIDER_FILTER_KEY,
		ref<string[] | null>(null)
	);
	provideLocal(PROVIDER_EXP_BB_PROVIDER_FILTER_KEY, effectiveFilter);

	const lastBuyboxTypeValue = injectLocal<Ref<0 | 1>>(PROVIDER_EXP_BB_LAST_TYPE_KEY, ref<0 | 1>(0));
	provideLocal(PROVIDER_EXP_BB_LAST_TYPE_KEY, lastBuyboxTypeValue);

	// EXP_PROVIDER_BUYBOX_V2: whether the provider modal has already been shown for the title
	// currently being viewed. Resets on title change and on reset, so the very next My Services
	// click always opens the modal again even if providers are already saved.
	const hasShownModalForTitle = injectLocal<Ref<boolean>>(PROVIDER_EXP_BB_HAS_SHOWN_MODAL_KEY, ref(false));
	provideLocal(PROVIDER_EXP_BB_HAS_SHOWN_MODAL_KEY, hasShownModalForTitle);
	if (options?.titleId) {
		watch(
			() => toValue(options.titleId),
			() => {
				hasShownModalForTitle.value = false;
			}
		);
	}
	// EXP_PROVIDER_BUYBOX_V2

	const showSavePrompt = injectLocal<Ref<boolean>>(PROVIDER_EXP_BB_SAVE_PROMPT_KEY, ref(false));
	provideLocal(PROVIDER_EXP_BB_SAVE_PROMPT_KEY, showSavePrompt);
	const { start: startSavePromptTimer } = useTimeoutFn(
		() => {
			showSavePrompt.value = false;
		},
		5000,
		{ immediate: false }
	);
	if (options?.offerProviders) {
		provideLocal(
			PROVIDER_EXP_BB_OFFER_PROVIDERS_KEY,
			computed(() => toValue(options.offerProviders!))
		);
	}
	const buyboxOfferProviders = options?.offerProviders
		? computed(() => toValue(options.offerProviders!))
		: injectLocal<ComputedRef<string[]> | null>(PROVIDER_EXP_BB_OFFER_PROVIDERS_KEY, null);

	const selectedBuyboxProviders = computed<string[] | null>(() => {
		const variant = providerBuyboxExpVariant.value;
		if (!variant || variant === 'control') return null;
		if (options?.listFilterType) {
			return toValue(options.listFilterType) === BuyBoxFilterOptions.MY_SERVICES
				? settingsProvider.value ?? []
				: null;
		}
		// Use effectiveFilter only as an activation flag (null = chip not yet clicked).
		// Read settingsProvider reactively so modal updates are reflected immediately.
		return effectiveFilter.value === null ? null : settingsProvider.value ?? [];
	});

	const myServicesChip = computed(() => {
		const variant = providerBuyboxExpVariant.value;
		if (!variant || variant === 'control') return null;
		const label = tc('WEBAPP_LISTS_MY_SERVICES');
		// EXP_PROVIDER_BUYBOX_V2: variant_1 and variant_2 show the selected provider count instead of icons
		if (['variant_1', 'variant_2'].includes(variant)) {
			const count = settingsProvider.value?.length ?? 0;
			return { label: count > 0 ? `${label} (${count})` : label, value: 'my_services' };
		}
		return { label, value: 'my_services' };
	});

	const myServicesActive = computed(() => effectiveFilter.value !== null);

	function deactivateMyServices() {
		if (!myServicesActive.value) return;
		effectiveFilter.value = null;
	}

	async function openMyServicesModal(
		buyboxType: 'grid' | 'list'
	): Promise<{ wasReset: boolean; confirmedProviders: Package[] | null }> {
		let wasReset = false;
		let wasGuestContinue = false;
		let confirmedProviders: Package[] | null = null;

		const _buyboxType: 0 | 1 = buyboxType === 'list' ? 1 : 0;
		lastBuyboxTypeValue.value = _buyboxType;

		trackEvent({
			action: 'click',
			label: 'provider_filter_open',
			value: _buyboxType,
		});

		await ModalHelper.openModal(
			PackagesSelectionModal,
			{
				providerPlace: ProviderFilterPlace.BUYBOX,
				offerProviders: buyboxOfferProviders?.value,
				expVariant: providerBuyboxExpVariant.value,
				onBuyboxProviderToggle: (packageItem: { technicalName: string }) => {
					trackEvent({
						action: 'click',
						label: 'provider_toggle',
						property: packageItem.technicalName,
						value: _buyboxType,
					});
				},
				onSearchbarFocus: () => {
					trackEvent({
						action: 'click',
						label: 'provider_search_focus',
						value: _buyboxType,
					});
				},
				onBuyboxSearched: (term: string) => {
					trackEvent({
						action: 'click',
						label: 'provider_search',
						property: term,
						value: _buyboxType,
					});
				},
				...(providerBuyboxExpVariant.value !== 'control'
					? {
							onBuyboxFilterReset: () => {
								wasReset = true;
								// EXP_PROVIDER_BUYBOX_V2: reset immediately so the chip deactivates
								effectiveFilter.value = null;
								// EXP_PROVIDER_BUYBOX_V2: force the modal to reopen on the next My Services click
								hasShownModalForTitle.value = false;
								trackEvent({
									action: 'click',
									label: 'reset',
									value: _buyboxType,
								});
							},
							onBuyboxFilterCancel: () => {
								trackEvent({
									action: 'click',
									label: 'provider_filter_cancel',
									value: _buyboxType,
								});
							},
							onBuyboxFilterDismiss: () => {
								trackEvent({
									action: 'click',
									label: 'provider_filter_close',
									value: _buyboxType,
								});
							},
							onBuyboxFilterConfirm: (providers: Package[]) => {
								confirmedProviders = providers;
								trackEvent({
									action: 'click',
									label: 'provider_filter_done',
									property: providers.map(provider => provider.technicalName).join(', '),
									value: _buyboxType,
								});
							},
							onBuyboxFilterActivate: (providers: Package[]) => {
								confirmedProviders = providers;
							},
							// EXP_PROVIDER_BUYBOX_V2: account creation is now inline in the modal
							...(!userStore.isLoggedIn
								? {
										onCreateAccount: (providers: Package[]) => {
											trackEvent({
												action: 'click',
												label: 'create_account',
												property: providers.map(p => p.technicalName).join(', '),
												value: _buyboxType,
											});
											ModalHelper.openModal(
												AccountModal,
												{
													closable: true,
													startScreen: AccountModes.SIGN_UP,
													expVariant: providerBuyboxExpVariant.value,
												},
												{ cssClass: 'account-modal' }
											);
										},
										onContinueWithoutAccount: (providers: Package[]) => {
											wasGuestContinue = true;
											trackEvent({
												action: 'click',
												label: 'continue_no_account',
												property: providers.map(p => p.technicalName).join(', '),
												value: _buyboxType,
											});
										},
								  }
								: {}),
					  }
					: {}),
			},
			{ cssClass: 'packages-selection-modal' },
			ProviderFilterPlace.BUYBOX,
			true
		);

		if (wasGuestContinue) {
			showSavePrompt.value = true;
			startSavePromptTimer();
		}

		return { wasReset, confirmedProviders };
	}

	function onSaveServicesSignUp() {
		const value = lastBuyboxTypeValue.value;
		trackEvent({
			action: 'click',
			label: 'create_account',
			property: 'pop_up',
			value,
		});
		ModalHelper.openModal(
			AccountModal,
			{
				closable: true,
				startScreen: AccountModes.SIGN_UP,
				expVariant: providerBuyboxExpVariant.value,
			},
			{ cssClass: 'account-modal' }
		);
	}

	async function handleMyServicesChipClick(
		buyboxType: 'grid' | 'list',
		isCurrentlyActive: boolean
	): Promise<boolean> {
		// EXP_PROVIDER_BUYBOX_V2: if the tab isn't already active, the user already has providers saved,
		// AND we've already shown the modal once for this title, switch to it directly without showing
		// the selection modal. Re-clicking while already active still opens the modal so the user can
		// edit their selection, and the very first click on a given title always opens it too.
		if (!isCurrentlyActive && hasShownModalForTitle.value && (settingsProvider.value?.length ?? 0) > 0) {
			effectiveFilter.value = settingsProvider.value ?? [];
			setSeenMyServices();
			return true;
		}

		// EXP_PROVIDER_BUYBOX_V2: mark as shown before opening — onBuyboxFilterReset flips it back
		// to false if the user resets their selection from within this same modal session.
		hasShownModalForTitle.value = true;
		const { wasReset, confirmedProviders } = await openMyServicesModal(buyboxType);
		setSeenMyServices();
		if (wasReset || confirmedProviders === null) return false;
		const shouldActivate = confirmedProviders.length > 0;
		effectiveFilter.value = shouldActivate ? confirmedProviders.map(p => p.shortName) : null;
		return shouldActivate;
	}

	return {
		selectedBuyboxProviders,
		providerBuyboxExpVariant,
		deactivateMyServices,
		myServicesChip,
		myServicesActive,
		handleMyServicesChipClick,
		showSavePrompt,
		onSaveServicesSignUp,
	};
}
